import React, { useState } from 'react';
import 'bootswatch/dist/minty/bootstrap.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  useListPublicArticlesQuery,
  useCreatePublicReservationMutation,
} from '../../redux/api/reservationApi';
import { formatApiError } from '../../hooks/useApiError';

const AVAILABILITY = {
  available: { label: 'Verfügbar', cls: 'bg-success' },
  low: { label: 'Wenige verfügbar', cls: 'bg-warning text-dark' },
  sold_out: { label: 'Ausverkauft', cls: 'bg-secondary' },
};

// Pickup window (ISO YYYY-MM-DD). Out-of-range dates are blocked in the date
// picker (min/max) and re-validated on submit for manually typed input.
const PICKUP_EARLIEST = '2026-06-14';
const PICKUP_LATEST = '2026-06-22';

// 'YYYY-MM-DD' -> 'TT.MM.JJJJ' for German-facing messages.
const formatDe = (iso) => {
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
};

const EMPTY = {
  customer_name: '',
  customer_email: '',
  customer_phone: '',
  note: '',
  pickup_until: '',
};

const PublicReservation = () => {
  const { data: articles, isLoading } = useListPublicArticlesQuery();
  const [createPublicReservation, { isLoading: submitting }] =
    useCreatePublicReservationMutation();

  const [cart, setCart] = useState({}); // articleId -> quantity
  const [form, setForm] = useState(EMPTY);
  const [done, setDone] = useState(false);

  const setField = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const setQty = (id, value) => {
    const n = parseInt(value, 10);
    setCart((prev) => {
      const next = { ...prev };
      if (isNaN(n) || n < 1) delete next[id];
      else next[id] = n;
      return next;
    });
  };

  const chosen = (articles || []).filter((a) => cart[a.id] > 0);

  // Sum goods value and deposit (Pfand) separately; deposit is charged on top
  // of the price, so the grand total is the sum of both.
  const totals = chosen.reduce(
    (acc, a) => {
      const qty = cart[a.id] || 0;
      acc.goods += Number(a.price || 0) * qty;
      acc.deposit += (a.deposit && a.deposit_amount ? Number(a.deposit_amount) : 0) * qty;
      return acc;
    },
    { goods: 0, deposit: 0 }
  );
  const grandTotal = totals.goods + totals.deposit;
  const eur = (n) => `${n.toFixed(2)} €`;

  const submit = async () => {
    if (!form.customer_name.trim() || !form.customer_email.trim()) {
      toast.error('Bitte Name/Crew und E-Mail angeben.');
      return;
    }
    if (chosen.length === 0) {
      toast.error('Bitte mindestens einen Artikel auswählen.');
      return;
    }
    if (form.pickup_until) {
      if (form.pickup_until < PICKUP_EARLIEST) {
        toast.error(`Keine Abholung vor dem ${formatDe(PICKUP_EARLIEST)} möglich.`);
        return;
      }
      if (form.pickup_until > PICKUP_LATEST) {
        toast.error(`Keine Abholung nach dem ${formatDe(PICKUP_LATEST)} möglich.`);
        return;
      }
    }
    const payload = {
      customer_name: form.customer_name.trim(),
      customer_email: form.customer_email.trim(),
      customer_phone: form.customer_phone.trim(),
      note: form.note.trim(),
      pickup_until: form.pickup_until || null,
      items: chosen.map((a) => ({ article: a.id, quantity: cart[a.id] })),
    };
    try {
      await createPublicReservation(payload).unwrap();
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const resetAll = () => {
    setCart({});
    setForm(EMPTY);
    setDone(false);
  };

  return (
    <div className='App'>
      <nav className='navbar navbar-dark bg-primary mb-4'>
        <div className='container'>
          <span className='navbar-brand'>Getränkemarkt · Reservierung</span>
        </div>
      </nav>

      <div className='container text-start' style={{ maxWidth: 720 }}>
        <div className='alert alert-warning'>
          Bitte beachtet, dass die Anfrage hier <b>keine verbindliche Bestellung</b> ist und wir uns nochmal per E-Mail mit einer manuellen Bestätigung bei euch melden, bevor abgeholt werden kann.<br/><br/>
          <b>Öffnungszeiten:</b> Jeweils von 16 - 20 Uhr (noch bis 22.06.)
        </div>
        {done ? (
          <div className='card'>
            <div className='card-body text-center'>
              <i className='fas fa-circle-check fa-3x text-success mb-3' />
              <h4>Anfrage eingegangen</h4>
              <p className='text-muted'>
                Vielen Dank! Deine Reservierung wird von uns geprüft, wir melden uns bei dir per E-Mail. Du kannst die Ware dann vor Ort abholen und bezahlen, wenn wir die Reservierung bestätigen.
              </p>
              <button className='btn btn-outline-primary' onClick={resetAll}>
                Weitere Reservierung
              </button>
            </div>
          </div>
        ) : (
          <>
            <p className='text-muted'>
              Reserviere Getränke zur Abholung. Bezahlt und abgeholt wird vor Ort.
              Deine Anfrage wird geprüft und bestätigt, wenn wir sie erfüllen können.
            </p>

            <h5 className='mt-4'>Artikel</h5>
            {isLoading ? (
              <p>Wird geladen…</p>
            ) : (articles || []).length === 0 ? (
              <div className='alert alert-light'>
                Aktuell sind keine Artikel reservierbar.
              </div>
            ) : (
              <div className='list-group mb-4'>
                {articles.map((a) => {
                  const avail = AVAILABILITY[a.availability_state] || AVAILABILITY.sold_out;
                  const soldOut = a.availability_state === 'sold_out';
                  return (
                    <div key={a.id} className='list-group-item'>
                      <div className='d-flex justify-content-between align-items-center'>
                        <div>
                          <strong>{a.name}</strong>
                          <span className={`badge ms-2 ${avail.cls}`}>
                            {avail.label}
                          </span>
                          <div className='small text-muted'>
                            {Number(a.price || 0).toFixed(2)} €
                            {a.deposit && a.deposit_amount
                              ? ` (+ ${Number(a.deposit_amount).toFixed(2)} € Pfand)`
                              : ''}{' '}
                            / {a.unit}
                          </div>
                        </div>
                        <input
                          type='number'
                          min='0'
                          className='form-control'
                          style={{ width: 90 }}
                          disabled={soldOut}
                          placeholder='Menge'
                          value={cart[a.id] || ''}
                          onChange={(e) => setQty(a.id, e.target.value)}
                          onWheel={(e) => e.target.blur()}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {chosen.length > 0 && (
              <div className='alert alert-info'>
                <div className='d-flex justify-content-between'>
                  <span>Warenwert</span>
                  <span>{eur(totals.goods)}</span>
                </div>
                {totals.deposit > 0 && (
                  <div className='d-flex justify-content-between'>
                    <span>Pfand</span>
                    <span>{eur(totals.deposit)}</span>
                  </div>
                )}
                <hr className='my-2' />
                <div className='d-flex justify-content-between fw-bold'>
                  <span>Gesamt (vor Ort zu zahlen)</span>
                  <span>{eur(grandTotal)}</span>
                </div>
                {totals.deposit > 0 && (
                  <div className='small text-muted mt-1'>
                    Davon {eur(totals.deposit)} Pfand, das bei Rückgabe erstattet wird.
                  </div>
                )}
              </div>
            )}

            <h5>Kontakt</h5>
            <div className='row g-2 mb-3'>
              <div className='col-md-6'>
                <label className='form-label'>Name/Crew *</label>
                <input
                  className='form-control'
                  value={form.customer_name}
                  onChange={setField('customer_name')}
                />
              </div>
              <div className='col-md-6'>
                <label className='form-label'>E-Mail *</label>
                <input
                  type='email'
                  className='form-control'
                  value={form.customer_email}
                  onChange={setField('customer_email')}
                />
              </div>
              <div className='col-md-6'>
                <label className='form-label'>Telefon <span className='text-muted'>(optional)</span></label>
                <input
                  className='form-control'
                  value={form.customer_phone}
                  onChange={setField('customer_phone')}
                />
              </div>
              <div className='col-md-6'>
                <label className='form-label'>Abholung am <span className='text-muted'>(optional, aber sehr hilfreich)</span></label>
                <input
                  type='date'
                  className='form-control'
                  min={PICKUP_EARLIEST}
                  max={PICKUP_LATEST}
                  value={form.pickup_until}
                  onChange={setField('pickup_until')}
                />
              </div>
              <div className='col-12'>
                <label className='form-label'>Notiz <span className='text-muted'>(optional)</span></label>
                <input
                  className='form-control'
                  value={form.note}
                  onChange={setField('note')}
                />
              </div>
            </div>

            <button
              className='btn btn-success btn-lg w-100 mb-5'
              onClick={submit}
              disabled={submitting}
            >
              <i className='fas fa-paper-plane me-2' />
              Reservierung anfragen
            </button>
          </>
        )}
      </div>

      <ToastContainer autoClose={4000} position='top-right' theme='colored' />
    </div>
  );
};

export default PublicReservation;
